import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import {
    loadNextPage,
    resetAndLoadFirstPage,
    markAllAdminNotificationsRead,
    loadAdminNotificationCounts,
} from "../../actions/adminNotificationActions";
import { notificationTypes, notificationFilters, formatNotificationCount, getNotificationId } from "../../utils/adminNotificationUtils";
import AdminNotificationItem from "./AdminNotificationItem";

const AdminNotificationsPanel = ({ open, setOpen, onOpenNotification }) => {
    const dispatch = useDispatch();
    const { notifications, unreadCount, unreadByType, hasMore, filters, loading, error, countsError } = useSelector((state) => state.notification);
    const [mounted, setMounted] = useState(open);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [from, setFrom] = useState(filters.from || "");
    const [to, setTo] = useState(filters.to || "");
    const [sort, setSort] = useState(filters.sort || "desc");
    const [actionError, setActionError] = useState("");
    const [readingAll, setReadingAll] = useState(false);
    const panelRef = useRef(null);
    const closeRef = useRef(null);
    const listRef = useRef(null);
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (open) {
            setMounted(true);
            setActionError("");
            dispatch(resetAndLoadFirstPage());
            return undefined;
        }
        const timer = setTimeout(() => setMounted(false), 280);
        return () => clearTimeout(timer);
    }, [open, dispatch]);

    useEffect(() => {
        if (!open || !mounted) return undefined;
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();
        const onKey = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
            }
            if (event.key !== "Tab") return;
            const elements = Array.from(panelRef.current?.querySelectorAll("button, a[href], input, select, [tabindex]") || [])
                .filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length > 0);
            const first = elements[0];
            const last = elements[elements.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", onKey, true);
            if (previousFocus?.isConnected) previousFocus.focus();
        };
    }, [open, mounted, setOpen]);

    useEffect(() => {
        if (!open || !mounted || !hasMore || loading || error || typeof IntersectionObserver === "undefined") return undefined;
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) dispatch(loadNextPage());
        }, { root: listRef.current, rootMargin: "160px" });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [open, mounted, hasMore, loading, error, notifications.length, dispatch]);

    const applyFilters = (event) => {
        event.preventDefault();
        if (from && to && from > to) {
            setActionError("Начальная дата не может быть позже конечной.");
            return;
        }
        setActionError("");
        dispatch(resetAndLoadFirstPage({ from: from || null, to: to || null, sort }));
    };
    const resetFilters = () => {
        setFrom("");
        setTo("");
        setSort("desc");
        setActionError("");
        dispatch(resetAndLoadFirstPage({ ...notificationFilters }));
    };
    const readAll = async () => {
        if (readingAll) return;
        setReadingAll(true);
        setActionError("");
        const ok = await dispatch(markAllAdminNotificationsRead());
        setReadingAll(false);
        if (!ok) setActionError("Не удалось прочитать все уведомления. Попробуйте ещё раз.");
    };

    if (!mounted) return null;

    return createPortal(
        <div className={"admin-notifications-root " + (open ? "is-open" : "is-closing")} aria-hidden={!open}>
            <div className="admin-notifications-backdrop" onClick={() => setOpen(false)} />
            <aside ref={panelRef} id="admin-notifications-panel" className="notification-panel" role="dialog" aria-modal="true" aria-labelledby="admin-notifications-title">
                <div className="notification-panel__header">
                    <div>
                        <h3 id="admin-notifications-title">Уведомления</h3>
                        <small>Непрочитанных: {unreadCount}</small>
                    </div>
                    <div className="notification-panel__header-actions">
                        {unreadCount > 0 && (
                            <button type="button" className="notification-panel__button" disabled={readingAll} title="Все уведомления за всё время, включая другие типы" onClick={readAll}>
                                {readingAll ? "Сохранение…" : "Прочитать всё"}
                            </button>
                        )}
                        <button ref={closeRef} type="button" className="notification-panel__close" aria-label="Закрыть уведомления" onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </button>
                    </div>
                </div>
                <div className="notification-panel__types">
                    {notificationTypes.map((item) => {
                        const count = item.key ? unreadByType[item.key] : unreadCount;
                        return (
                            <button key={item.key} type="button" className={filters.type === item.key ? "is-active" : ""} aria-pressed={filters.type === item.key} onClick={() => dispatch(resetAndLoadFirstPage({ type: item.key }))}>
                                {item.label}
                                {count > 0 && <b>{formatNotificationCount(count)}</b>}
                            </button>
                        );
                    })}
                </div>
                <div className="notification-panel__quick">
                    <label>
                        <input type="checkbox" checked={!!filters.unread} onChange={(event) => dispatch(resetAndLoadFirstPage({ unread: event.target.checked }))} />
                        Только непрочитанные
                    </label>
                    <button type="button" className="notification-panel__button" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((value) => !value)}>
                        Фильтры
                    </button>
                </div>
                {filtersOpen && (
                    <form className="notification-panel__filters" onSubmit={applyFilters}>
                        <label>С даты<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
                        <label>По дату<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
                        <label className="notification-panel__sort">Порядок
                            <select value={sort} onChange={(event) => setSort(event.target.value)}>
                                <option value="desc">Сначала новые</option>
                                <option value="asc">Сначала старые</option>
                            </select>
                        </label>
                        <div className="notification-panel__filter-actions">
                            <button type="button" className="notification-panel__button" onClick={resetFilters}>Сбросить</button>
                            <button type="submit" className="notification-panel__button is-primary">Применить</button>
                        </div>
                        <small>Счётчики непрочитанных — за всё время.</small>
                    </form>
                )}
                {(actionError || countsError) && (
                    <div className="notification-panel__error" role="alert">
                        {actionError || countsError}
                        {countsError && <button type="button" onClick={() => dispatch(loadAdminNotificationCounts())}>Повторить</button>}
                    </div>
                )}
                <div ref={listRef} className="notification-panel__list">
                    {notifications.map((item) => <AdminNotificationItem key={getNotificationId(item)} data={item} onOpenNotification={onOpenNotification} />)}
                    {loading && <p className="notification-panel__empty" role="status">Загрузка уведомлений…</p>}
                    {!loading && !error && notifications.length === 0 && <p className="notification-panel__empty">Нет уведомлений по выбранным фильтрам</p>}
                    {error && <p className="notification-panel__error" role="alert">{error}</p>}
                    {(hasMore || error) && (
                        <div ref={sentinelRef} className="notification-panel__footer">
                            <button type="button" className="notification-panel__button" disabled={loading} onClick={() => dispatch(loadNextPage())}>
                                {error ? "Повторить загрузку" : loading ? "Загрузка…" : "Загрузить ещё"}
                            </button>
                        </div>
                    )}
                    {!hasMore && notifications.length > 0 && <p className="notification-panel__empty">Все уведомления загружены</p>}
                </div>
            </aside>
        </div>,
        document.body
    );
};

export default AdminNotificationsPanel;
